// SSDP discovery of Roku devices on the local network, then ECP /query/device-info
// per discovered IP to match back to a known serial. Mirrors homeAlertsApp's
// webapp/js/RokuDiscovery.js. Native-only (needs Capacitor UdpSocket plugin and
// cordova.plugin.http to bypass WKWebView cross-origin restrictions).

const SSDP_ADDRESS = '239.255.255.250';
const SSDP_PORT = 1900;
const ECP_PORT = 8060;
const SCAN_TIMEOUT_MS = 5000;

function getUdpSocket() {
  return window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.UdpSocket;
}

function getHttp() {
  return window.cordova && window.cordova.plugin && window.cordova.plugin.http;
}

export function cachedRokuIp(serial) {
  if (!serial) return '';
  try { return sessionStorage.getItem('roku_ip_' + serial) || localStorage.getItem('roku_ip_' + serial) || ''; }
  catch (e) { return ''; }
}

function cacheRokuIp(serial, ip) {
  if (!serial || !ip) return;
  try { sessionStorage.setItem('roku_ip_' + serial, ip); } catch (e) {}
  try { localStorage.setItem('roku_ip_' + serial, ip); } catch (e) {}
}

function parseDeviceInfoXml(xmlText, ip) {
  try {
    const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
    const tag = t => {
      const el = doc.getElementsByTagName(t)[0];
      return el ? el.textContent : '';
    };
    return {
      ip,
      serial: tag('serial-number'),
      name: tag('user-device-name') || tag('friendly-device-name') || tag('model-name'),
      model: tag('model-name'),
      softwareVersion: tag('software-version'),
      wifiMac: tag('wifi-mac')
    };
  } catch (e) {
    return null;
  }
}

function queryDeviceInfo(ip) {
  return new Promise(resolve => {
    const http = getHttp();
    if (!http) { resolve(null); return; }
    http.sendRequest(
      `http://${ip}:${ECP_PORT}/query/device-info`,
      { method: 'get', responseType: 'text', connectTimeout: 3, readTimeout: 3 },
      resp => {
        if (resp.status === 200 && resp.data) {
          resolve(parseDeviceInfoXml(resp.data, ip));
        } else {
          resolve(null);
        }
      },
      () => resolve(null)
    );
  });
}

// Scan and return an array of {ip, serial, name, model, ...}.
// Each discovered device's IP is also cached by serial for later lookups.
export async function scanRokus() {
  const UdpSocket = getUdpSocket();
  if (!UdpSocket) { throw new Error('UDP socket plugin unavailable (native-only)'); }

  const discoveredIPs = [];
  let socketId = -1;
  let receiveHandle = null;
  let errorHandle = null;

  try {
    // Pass an explicit empty `properties` object — the Android plugin's
    // setProperties() NPEs on null, while iOS tolerates it. This is the
    // actual cause of the Android "create error" you'll see in logcat
    // (UdpSocket.java:108 inside setProperties).
    const info = await UdpSocket.create({ properties: {} });
    socketId = (info && info.socketId !== undefined) ? info.socketId : -1;
    if (socketId < 0) { throw new Error('Invalid socketId'); }

    await UdpSocket.bind({ socketId, port: 0 });

    receiveHandle = await UdpSocket.addListener('receive', data => {
      if (!data || !data.remoteAddress) return;
      if (discoveredIPs.indexOf(data.remoteAddress) !== -1) return;
      let message = '';
      try { message = atob(data.buffer); } catch (e) { message = data.buffer || ''; }
      if (message.toLowerCase().indexOf('roku') > -1 || message.toLowerCase().indexOf('ecp') > -1) {
        discoveredIPs.push(data.remoteAddress);
      }
    });
    errorHandle = await UdpSocket.addListener('receiveError', () => {});

    try { await UdpSocket.joinGroup({ socketId, address: SSDP_ADDRESS }); } catch (e) {}
    await new Promise(r => setTimeout(r, 100));

    const ssdp =
      'M-SEARCH * HTTP/1.1\r\n' +
      `Host: ${SSDP_ADDRESS}:${SSDP_PORT}\r\n` +
      'Man: "ssdp:discover"\r\n' +
      'ST: roku:ecp\r\n' +
      'MX: 3\r\n\r\n';

    await UdpSocket.send({
      socketId,
      address: SSDP_ADDRESS,
      port: SSDP_PORT,
      buffer: btoa(ssdp)
    });

    await new Promise(r => setTimeout(r, SCAN_TIMEOUT_MS));
  } finally {
    try { if (receiveHandle) receiveHandle.remove(); } catch (e) {}
    try { if (errorHandle) errorHandle.remove(); } catch (e) {}
    if (socketId >= 0) {
      try { await UdpSocket.leaveGroup({ socketId, address: SSDP_ADDRESS }); } catch (e) {}
      try { await UdpSocket.close({ socketId }); } catch (e) {}
    }
  }

  const devices = (await Promise.all(discoveredIPs.map(queryDeviceInfo))).filter(Boolean);
  devices.forEach(d => cacheRokuIp(d.serial, d.ip));
  return devices;
}

// Resolve the LAN IP for a given Roku serial.
// 1) sessionStorage/localStorage cache (from prior scans)
// 2) Full SSDP scan, then match by serial
export async function resolveRokuIp(serial) {
  const cached = cachedRokuIp(serial);
  if (cached) return cached;

  const devices = await scanRokus();
  const match = devices.find(d => d.serial === serial);
  return match ? match.ip : '';
}
